import React, { useRef, useEffect, useLayoutEffect, useState, useCallback } from "react";
import classnames from "classnames";
import { observer } from "mobx-react";
import Portal from "../Portal/Portal";
import "./Tooltip.css";


type ITooltip = {
  tooltipContent?: React.ReactNode;
  children?: React.ReactNode[] | React.ReactNode;
  tooltipText?: string;
  trigger?: "mouseover" | "click";
  wrapperClassName?: string;
  tooltipClassName?: string;
  position?: "down" | "up";
  duration?: number;
  size?: "small";
  disabled?: boolean;
}

interface IA11KeyboardClickOptions {
  disableSpacebarClick?: boolean;
}

interface IA11KeyboardClick {
  event: React.KeyboardEvent | KeyboardEvent;
  eventHandler: (...args:any) => any;
  options?: IA11KeyboardClickOptions;
}

const Tooltip: React.FunctionComponent<ITooltip> = ({
  tooltipContent,
  children,
  tooltipText,
  trigger = "click",
  wrapperClassName,
  tooltipClassName,
  position = "down",
  duration,
  size,
  disabled,
}) => {
  const tooltipTrigger = useRef<HTMLDivElement>(null);
  const tooltipElement = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const tooltipPositionOffset = 8;

  const KEY_CODES = {
    leftArrow: 37,
    rightArrow: 39,
    enterKey: 13,
    spacebar: 32,
  };

  const a11yKeyboardClick = ({ event, eventHandler, options = { disableSpacebarClick: false } }: IA11KeyboardClick) => {
    const pressedKey = event.keyCode;
    if (pressedKey === KEY_CODES.enterKey || (!options?.disableSpacebarClick && pressedKey === KEY_CODES.spacebar)) {
      event.preventDefault();
      // This prevents the whole page from scrolling when you press Spacebar, which is what regular buttons do in HTML and that's desired behavior for elements you can press with Spacebar
      eventHandler(event);
    }
  };

  const handleHideDropdown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      setIsVisible(false);
    }
  };

  const calculateFinalPosition = useCallback(
    (triggerElement) => {
      const finalPosition = {
        top: 0,
        left: 0,
      };

      if (!tooltipElement.current) {
        return finalPosition;
      }

      const tooltipDimensions = tooltipElement.current.getBoundingClientRect();

      const targetRect = triggerElement.getBoundingClientRect();

      switch (position) {
        case "up":
          finalPosition.top = targetRect.top - tooltipDimensions.height - tooltipPositionOffset;
          finalPosition.left = targetRect.left - tooltipDimensions.width + 20;
          break;

        case "down":
          finalPosition.top = targetRect.top + targetRect.height + tooltipPositionOffset;
          finalPosition.left = targetRect.left - tooltipDimensions.width + 20;
          break;

        default:
          break;
      }

      return {
        top: finalPosition.top,
        left: finalPosition.left,
      };
    },
    [position]
  );

  const handleTooltipVisibility = useCallback(
    (e) => {
      if (trigger === "click") {
        setIsVisible((currentState) => !currentState);
      } else {
        if (!duration && e.type === "mouseout") {
          setIsVisible(false);
        } else {
          setIsVisible(true);
        }
      }

      const targetPosition = calculateFinalPosition(tooltipTrigger.current);
      setTooltipPosition({ ...targetPosition });
    },
    [calculateFinalPosition, trigger, duration]
  );

  useEffect(() => {
    if (!isVisible || !duration) return;

    const timer = setTimeout(() => {
      setIsVisible(false);
    }, duration);

    return () => {
      clearTimeout(timer);
    };
  }, [isVisible, duration]);

  useLayoutEffect(() => {
    if (!tooltipTrigger.current || disabled) return;

    // Needed for cleanup process
    const triggerElement = tooltipTrigger.current;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipTrigger.current &&
        !tooltipTrigger.current.contains(event.target as Node) &&
        tooltipElement.current &&
        !tooltipElement.current.contains(event.target as Node)
      ) {
        setIsVisible(false);
      }
    };

    const handleTooltipKeyboardClick = (event: KeyboardEvent) => {
      a11yKeyboardClick({
        event,
        eventHandler: handleTooltipVisibility,
        options: { disableSpacebarClick: true },
      });
    };

    triggerElement.addEventListener(trigger, handleTooltipVisibility);
    triggerElement.addEventListener("keyup", handleTooltipKeyboardClick);
    document.addEventListener("keydown", handleHideDropdown);
    document.addEventListener("click", handleClickOutside);
    if (trigger === "mouseover") {
      triggerElement.addEventListener("mouseout", handleTooltipVisibility)
    }

    return () => {
      triggerElement.removeEventListener(trigger, handleTooltipVisibility);
      triggerElement.removeEventListener("keyup", handleTooltipKeyboardClick);
      document.removeEventListener("keydown", handleHideDropdown);
      document.removeEventListener("click", handleClickOutside);
      if (trigger === "mouseover") {
        triggerElement.addEventListener("mouseout", handleTooltipVisibility)
      }
    };
  }, [handleTooltipVisibility, trigger, disabled]);

  const tooltipPositionStyles: React.CSSProperties = {
    top: `${tooltipPosition.top}px`,
    left: `${tooltipPosition.left}px`,
  };

  const wrapperClasses = classnames("tooltip-wrapper", wrapperClassName);
  const tooltipElementClasses = classnames(
    "tooltip",
    `tooltip--${position}`,
    size ? `tooltip--${size}` : false,
    isVisible ? "tooltip--visible" : "tooltip--not-visible",
    tooltipClassName
  );

  return (
    <>
      {/* ESLint complains here that this is not an interactive element, but it's because it cannot detect event listeners that are added to tooltipTrigger above */}
      {/* eslint-disable-next-line */}
      <div className={wrapperClasses} ref={tooltipTrigger}>
        {children}
        <Portal selector="body">
          <div className={tooltipElementClasses} style={tooltipPositionStyles} ref={tooltipElement}>
            <div className="tooltip__content">
              {tooltipText}
              {tooltipContent}
            </div>
          </div>
        </Portal>
      </div>
    </>
  );
};

export default observer(Tooltip);
